/**
 * ItemDialogs Component
 * Dialogs for content item operations (move, duplicate, delete)
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Move, Copy, AlertTriangle, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { FolderSelector } from './FolderSelector'
import {
  useMoveContentItem,
  useDuplicateContentItem,
} from '@/lib/hooks/use-content-hub'
import type { ContentItem, ContentFolderTree } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface MoveItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemId: string
  itemName: string
  currentFolderId: string | null
}

interface DuplicateItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemId: string
  itemName: string
}

interface DeleteItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  itemName: string
  onConfirm: () => void
  isDeleting?: boolean
  usageCount?: number
}

// Legacy interfaces for backward compatibility
interface LegacyMoveItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ContentItem | null
  folderTree: ContentFolderTree[]
  onSubmit: (folderId: string | null) => Promise<void>
  isSubmitting?: boolean
}

interface LegacyDuplicateItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ContentItem | null
  folderTree: ContentFolderTree[]
  onSubmit: (data: { name: string; folderId?: string }) => Promise<void>
  isSubmitting?: boolean
}

interface LegacyDeleteItemDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  item: ContentItem | null
  onConfirm: () => Promise<void>
  isSubmitting?: boolean
}

// ============================================
// Helper Functions
// ============================================

function flattenFolderTree(
  folders: ContentFolderTree[],
  level = 0
): Array<{ id: string; name: string; level: number }> {
  const result: Array<{ id: string; name: string; level: number }> = []

  for (const folder of folders) {
    result.push({ id: folder.id, name: folder.name, level })

    if (folder.children && folder.children.length > 0) {
      result.push(...flattenFolderTree(folder.children, level + 1))
    }
  }

  return result
}

// ============================================
// MoveItemDialog Component (New Interface)
// ============================================

export function MoveItemDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  currentFolderId,
}: MoveItemDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(currentFolderId)
  const { toast } = useToast()

  const moveItem = useMoveContentItem({
    onSuccess: () => {
      toast({
        title: 'Item moved',
        description: `"${itemName}" has been moved successfully.`,
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to move item',
        variant: 'destructive',
      })
    },
  })

  useEffect(() => {
    if (open) {
      setSelectedFolderId(currentFolderId)
    }
  }, [open, currentFolderId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    moveItem.mutate({
      id: itemId,
      data: { folderId: selectedFolderId || undefined },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5 text-purple-500" />
              Move Item
            </DialogTitle>
            <DialogDescription>Move "{itemName}" to a different folder.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="move-folder">Destination Folder</Label>
              <FolderSelector
                value={selectedFolderId}
                onChange={setSelectedFolderId}
                placeholder="Select destination folder"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={moveItem.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={moveItem.isPending}>
              {moveItem.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Move Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// DuplicateItemDialog Component (New Interface)
// ============================================

export function DuplicateItemDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
}: DuplicateItemDialogProps) {
  const [name, setName] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { toast } = useToast()

  const duplicateItem = useDuplicateContentItem({
    onSuccess: (newItem) => {
      toast({
        title: 'Item duplicated',
        description: `"${name}" has been created successfully.`,
      })
      onOpenChange(false)
      // Navigate to the new item
      navigate(`/content-hub/item/${newItem.id}`)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to duplicate item',
        variant: 'destructive',
      })
    },
  })

  useEffect(() => {
    if (open) {
      setName(`${itemName} (Copy)`)
      setSelectedFolderId(null) // Will use same folder
    }
  }, [open, itemName])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    duplicateItem.mutate({
      id: itemId,
      data: {
        name: name.trim(),
        folderId: selectedFolderId || undefined,
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-blue-500" />
              Duplicate Item
            </DialogTitle>
            <DialogDescription>Create a copy of "{itemName}".</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="duplicate-name">Name</Label>
              <Input
                id="duplicate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name for the copy"
                autoFocus
                className="rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="duplicate-folder">Destination Folder (optional)</Label>
              <FolderSelector
                value={selectedFolderId}
                onChange={setSelectedFolderId}
                placeholder="Same folder as original"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={duplicateItem.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || duplicateItem.isPending}>
              {duplicateItem.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Copy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// DeleteItemDialog Component (New Interface)
// ============================================

export function DeleteItemDialog({
  open,
  onOpenChange,
  itemName,
  onConfirm,
  isDeleting = false,
  usageCount = 0,
}: DeleteItemDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete Item
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{itemName}"?
            {usageCount > 0 && (
              <span className="block mt-2 text-amber-600 dark:text-amber-400">
                This item is used in {usageCount} template(s). References will be broken.
              </span>
            )}
            <span className="block mt-2 font-medium text-red-600 dark:text-red-400">
              This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isDeleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Item
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ============================================
// Legacy Components (for backward compatibility)
// ============================================

export function LegacyMoveItemDialog({
  open,
  onOpenChange,
  item,
  folderTree,
  onSubmit,
  isSubmitting = false,
}: LegacyMoveItemDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string>('__root__')

  useEffect(() => {
    if (open && item) {
      setSelectedFolderId(item.folderId || '__root__')
    }
  }, [open, item])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(selectedFolderId === '__root__' ? null : selectedFolderId)
  }

  const flatFolders = flattenFolderTree(folderTree)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5 text-purple-500" />
              Move Item
            </DialogTitle>
            <DialogDescription>Move "{item?.name}" to a different folder.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="move-folder">Destination Folder</Label>
              <select
                id="move-folder"
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="__root__">Root (No Folder)</option>
                {flatFolders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {'  '.repeat(f.level)}
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Move Item
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function LegacyDuplicateItemDialog({
  open,
  onOpenChange,
  item,
  folderTree,
  onSubmit,
  isSubmitting = false,
}: LegacyDuplicateItemDialogProps) {
  const [name, setName] = useState('')
  const [selectedFolderId, setSelectedFolderId] = useState<string>('__same__')

  useEffect(() => {
    if (open && item) {
      setName(`${item.name} (Copy)`)
      setSelectedFolderId('__same__')
    }
  }, [open, item])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    await onSubmit({
      name: name.trim(),
      folderId:
        selectedFolderId === '__same__'
          ? item?.folderId ?? undefined
          : selectedFolderId === '__root__'
            ? undefined
            : selectedFolderId,
    })
  }

  const flatFolders = flattenFolderTree(folderTree)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-blue-500" />
              Duplicate Item
            </DialogTitle>
            <DialogDescription>Create a copy of "{item?.name}".</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="duplicate-name">Name</Label>
              <Input
                id="duplicate-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name for the copy"
                autoFocus
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="duplicate-folder">Destination Folder</Label>
              <select
                id="duplicate-folder"
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="__same__">Same Folder</option>
                <option value="__root__">Root (No Folder)</option>
                {flatFolders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {'  '.repeat(f.level)}
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isSubmitting}>
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Copy
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function LegacyDeleteItemDialog({
  open,
  onOpenChange,
  item,
  onConfirm,
  isSubmitting = false,
}: LegacyDeleteItemDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete Item
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete "{item?.name}"?
            {item && item.usageCount > 0 && (
              <span className="block mt-2 text-amber-600 dark:text-amber-400">
                This item is used in {item.usageCount} template(s). References will be broken.
              </span>
            )}
            <span className="block mt-2 font-medium text-red-600 dark:text-red-400">
              This action cannot be undone.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isSubmitting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Item
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default {
  MoveItemDialog,
  DuplicateItemDialog,
  DeleteItemDialog,
  LegacyMoveItemDialog,
  LegacyDuplicateItemDialog,
  LegacyDeleteItemDialog,
}
